/**
 * PRD §9.3 + REQ-094 — Requisition lifecycle including hiring-client gate (awaiting_client).
 */

import type { RequisitionStatus } from '@core/enums';

export const REQUISITION_STATUS_LABELS: Record<RequisitionStatus, string> = {
  draft: 'Draft',
  awaiting_client: 'Awaiting client',
  submitted: 'Submitted (internal approval)',
  approved: 'Approved',
  rejected: 'Rejected',
  sourcing: 'Sourcing (recruiting active)',
  filled: 'Filled',
  on_hold: 'On hold',
  cancelled: 'Cancelled',
};

/** Happy path including hiring-company confirmation before internal approval. */
export const REQUISITION_LIFECYCLE_PHASES: readonly { key: RequisitionStatus; short: string }[] = [
  { key: 'draft', short: 'Draft' },
  { key: 'awaiting_client', short: 'Client' },
  { key: 'submitted', short: 'Approve' },
  { key: 'approved', short: 'Approved' },
  { key: 'sourcing', short: 'Sourcing' },
  { key: 'filled', short: 'Filled' },
];

/** Titles + hints for horizontal/vertical flow stepper (PRD §9.3). */
export const REQUISITION_FLOW_STEPS: readonly {
  key: RequisitionStatus;
  title: string;
  description: string;
}[] = [
  {
    key: 'draft',
    title: 'Role intake',
    description: 'Capture client, role scope, skills, and logistics as a draft.',
  },
  {
    key: 'awaiting_client',
    title: 'Client gate',
    description: 'Email the hiring client; log replies until they confirm in writing.',
  },
  {
    key: 'submitted',
    title: 'Internal approval',
    description: 'Consultancy approvers review and accept the mandate.',
  },
  {
    key: 'approved',
    title: 'Approved',
    description: 'Create or link the job and prepare to go to market.',
  },
  {
    key: 'sourcing',
    title: 'Active sourcing',
    description: 'Recruiters source, screen, and move candidates through the pipeline.',
  },
  {
    key: 'filled',
    title: 'Filled',
    description: 'Hire complete — close the requisition when the role is staffed.',
  },
];

/** Index into REQUISITION_LIFECYCLE_PHASES / REQUISITION_FLOW_STEPS, or -1 if off happy path. */
export function getRequisitionFlowPhaseIndex(status: RequisitionStatus): number {
  if (status === 'rejected' || status === 'cancelled') return -1;
  if (status === 'on_hold') {
    const i = REQUISITION_LIFECYCLE_PHASES.findIndex((p) => p.key === 'sourcing');
    return i >= 0 ? i : 0;
  }
  const i = REQUISITION_LIFECYCLE_PHASES.findIndex((p) => p.key === status);
  return i >= 0 ? i : 0;
}

export function isRequisitionFlowOffHappyPath(status: RequisitionStatus): boolean {
  return status === 'rejected' || status === 'cancelled';
}

/**
 * Valid one-step transitions. Draft cannot jump straight to submitted except via
 * “Skip client gate” (service) or legacy tools — not in this map.
 */
export const REQUISITION_ALLOWED_TRANSITIONS: Record<RequisitionStatus, RequisitionStatus[]> = {
  draft: ['awaiting_client', 'cancelled'],
  /** → submitted only via “Record confirmation” in Hiring client gate (audited). */
  awaiting_client: ['draft', 'cancelled'],
  submitted: ['draft', 'approved', 'rejected', 'cancelled'],
  approved: ['sourcing', 'on_hold', 'cancelled'],
  rejected: ['draft', 'submitted'],
  sourcing: ['filled', 'on_hold', 'cancelled'],
  filled: [],
  on_hold: ['sourcing', 'cancelled'],
  cancelled: ['draft'],
};

export function isRequisitionTransitionAllowed(from: RequisitionStatus, to: RequisitionStatus): boolean {
  if (from === to) return true;
  return REQUISITION_ALLOWED_TRANSITIONS[from]?.includes(to) ?? false;
}

export function requisitionStatusEditOptions(current: RequisitionStatus): RequisitionStatus[] {
  const next = REQUISITION_ALLOWED_TRANSITIONS[current] ?? [];
  const set = new Set<RequisitionStatus>([current, ...next]);
  return Array.from(set);
}

export const REQUISITION_LIFECYCLE_HELPER =
  'After you create a requisition: email the hiring client for acknowledgement (status becomes Awaiting client). Log any replies. When the client confirms, record it — the request moves to internal approval (Submitted). Then approve, create/link a job, and run sourcing through Filled. Use On hold or Cancelled as needed.';
